// MARK://dhk8/pos/lib/api-origin
// Runtime decision: cloud-direct (Modo A) vs LAN-relay (Modo B). See
// docs/OPERATING-MODES.md §2. Decided once at boot from window.location.hostname.
// The ONLY thing that changes between modes is the API base URL — request/response
// shapes, Zod schemas, UI and stores are identical (OPERATING-MODES §3).
//
// Override for local dev / staging: set VITE_API_BASE (full origin, no trailing /v1).

export type OperatingMode = 'cloud-direct' | 'lan-relay'

const CLOUD_API_FALLBACK = 'https://api.dockethub.app'
const EDGE_HOSTNAME = 'nintaigo-edge.local'

const PRIVATE_IP_RE =
  /^(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/

/**
 * Deriva el API origin del host del browser: `admin.X.tld` → `https://api.X.tld`.
 * Cubre `nintaigo.com`, `dockethub.app`, etc. La cookie de auth vive en `.X.tld`
 * y se comparte con `api.X.tld`. Hardcoded dockethub.app rompe la sesión
 * cuando el browser está en `admin.nintaigo.com`.
 */
function cloudApiFromHost(host: string): string {
  const parts = host.split('.')
  if (parts.length < 3) return CLOUD_API_FALLBACK
  const [, ...rest] = parts
  return `https://api.${rest.join('.')}`
}

function detect(): { mode: OperatingMode; apiOrigin: string } {
  // Build-time / dev override wins.
  const override = import.meta.env.VITE_API_BASE as string | undefined
  if (override) {
    const isLan = /\.local$/.test(new URL(override).hostname) || PRIVATE_IP_RE.test(new URL(override).hostname)
    // Tolerar overrides con o sin `/v1` final. `apiBase()` siempre devuelve
    // `${apiOrigin}/v1`, así que si alguien pasó `VITE_API_BASE=…/v1` antes
    // teníamos URLs con doble `/v1/` (incidente 2026-05-22: admin no podía
    // hacer login porque la API request iba a `…/v1/v1/dhk8-…/v1/auth/…`).
    const cleaned = override.replace(/\/+$/, '').replace(/\/v1$/, '')
    return { mode: isLan ? 'lan-relay' : 'cloud-direct', apiOrigin: cleaned }
  }

  if (typeof window === 'undefined') {
    return { mode: 'cloud-direct', apiOrigin: CLOUD_API_FALLBACK }
  }

  const host = window.location.hostname

  if (host === EDGE_HOSTNAME || host.endsWith('.local') || PRIVATE_IP_RE.test(host)) {
    // Edge serves the API under the same origin it served the PWA from.
    return { mode: 'lan-relay', apiOrigin: window.location.origin }
  }

  // *.nintaigo.com → api.nintaigo.com · *.dockethub.app → api.dockethub.app …
  return { mode: 'cloud-direct', apiOrigin: cloudApiFromHost(host) }
}

const { mode: MODE, apiOrigin: API_ORIGIN } = detect()

/** The resolved operating mode for this session. */
export function operatingMode(): OperatingMode {
  return MODE
}

/** `true` when running against a NintaiGO Edge Server (Pi/NUC) on the LAN. */
export function isLanRelay(): boolean {
  return MODE === 'lan-relay'
}

/** API origin without trailing slash, e.g. `https://api.dockethub.app`. */
export function apiOrigin(): string {
  return API_ORIGIN
}

/** Versioned API base, e.g. `https://api.dockethub.app/v1`. */
export function apiBase(): string {
  return `${API_ORIGIN}/v1`
}

/** WebSocket endpoint used by the Edge orchestrator and live updates. */
export function wsBase(): string {
  return `${API_ORIGIN.replace(/^http/, 'ws')}/ws/v1`
}
